// src/components/ItineraryCreator/index.js with Model Selection, Translation Progress, and Batch Audio Generation

import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save, ChevronRight, MapPin, Workflow, PlusCircle, Languages, Loader, Volume2, FileText, Check, AlertCircle } from 'lucide-react';
import { computeRoutelegs, ROUTE_MODE_OPTIONS } from '../services/routeLegService';
import { saveItineraryToStorage } from '../ItineraryCreator/utils/storageUtils';
import { DEFAULT_MAP_DISPLAY_SETTINGS, generateId, generatePoiId, validatePoi, initialPoiState } from '../ItineraryCreator/utils/utils';
import { translatePoiFields } from '../ItineraryCreator/utils/translationUtils';
import { getCountryCode } from '../ItineraryCreator/utils/countryUtils';
import { prepareItineraryForPublication } from '../services/automaticGuideResearch';

// Access control imports
import { useAuth } from '../../contexts/AuthContext';
import { useAccessControl } from '../../contexts/AccessControlContext';
import PermissionManager from '../PermissionManager';
import FeatureGuard from '../FeatureGuard';

// Just import loadItineraryFromFile since you have your own saveItineraryToFile function
import { loadItineraryFromFile } from '../ItineraryCreator/utils/storageUtils';
import { buildKnowledgeContext } from '../ItineraryCreator/utils/knowledgeDocumentUtils';

import { MetadataSection } from '../ItineraryCreator/utils/MetadataSection';
import { EnhancedPOIForm } from '../forms/EnhancedPOIForm';
import { POIList } from '../itinerary/POIList';
import { ContextSection } from '../ItineraryCreator/tour/ContextSection';
import TourPlanningSection from '../ItineraryCreator/tour/TourPlanningSection';
import VoiceHeader from '../layout/VoiceHeader';
import ModelSelector from '../selectors/ModelSelector';
import TranslationModelSelector from '../selectors/TranslationModelSelector';
import TranslationProgress from '../ItineraryCreator/translation/TranslationProgress';
import BatchAudioGenerator from '../audio/BatchAudioGenerator';
import RAGDocumentManager from '../ItineraryCreator/rag/RAGDocumentManager';
import StoryAITab from '../ItineraryCreator/story/StoryAITab';

// Import language components
import { LANGUAGES, SUPPORTED_LANGUAGES } from '../selectors/LanguageSelector';
import { DEFAULT_TRANSLATION_MODEL, DESCRIPTION_TRANSLATION_INSTRUCTIONS } from '../constants/translationConfig';

import { useAudioGeneration } from '../hooks/useAudioGeneration';
import { useFieldGeneration } from '../hooks/useFieldGeneration';
import { useImageUpload } from '../hooks/useImageUpload';
import { useItineraryContent } from '../hooks/useItineraryContent';
import { useTourGeneration } from '../hooks/useTourGeneration';
import { useAiModel } from '../hooks/useAiModel';

import ItineraryMapView from '../itinerary/ItineraryMapView';

import { usePromptTemplates } from '../promptTemplates/index_prompt_templates.js';

// Import error handling utilities
import ErrorBoundary from '../layout/ErrorBoundary';
import patchReactDOM from '../ItineraryCreator/utils/DOMNodePatcher';
import SafeDOM from '../ItineraryCreator/utils/SafeDOM';
import { useLanguage } from './LanguageContext';

// Import shared language/voice utilities
import { getVoiceForLanguage, getLanguageName, createLanguageInstruction } from '../../utils/languageVoiceUtils';

const AccessDenied = ({ requiredAccess, feature }) => (
  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
    <span className="text-yellow-800">
      🔒 {feature} requires {requiredAccess} access
    </span>
  </div>
);

// Constants
export const POI_CATEGORIES = [
  'Historical',
  'Cultural',
  'Nature',
  'Museum',
  'Monument',
  'Religious Site',
  'Entertainment',
  'Restaurant',
  'Shopping',
  'Park',
  'Artwork',
  'Building',
  'Other'
];

const ItineraryCreator = () => {
  const { userRole } = useAuth();
  const { hasFieldAccess, getFieldAccess, canPerformAction } = useAccessControl();
const [showPermissionManager, setShowPermissionManager] = useState(false);

  const openAiKey = process.env.REACT_APP_OPENAI_API_KEY;
  const { language, setLanguage } = useLanguage();

  const { generatePoiAudio } = useAudioGeneration();
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  // Apply DOM patches on component mount
  useEffect(() => {
    patchReactDOM();
  }, []);

  // State for the active step
  const [activeStep, setActiveStep] = useState(2);

  // State for editing mode
  const [editingPoi, setEditingPoi] = useState(false);

  // State for showing context panel
  const [showContextPanel, setShowContextPanel] = useState(false);

  // State for batch audio generation
  const [showBatchAudio, setShowBatchAudio] = useState(false);

  // Get prompt utilities from the hook
  const { voiceStyle, setVoiceStyle, generatePromptForField } = usePromptTemplates();

  // Add AI model state using our custom hook
  const { currentModel, changeModel } = useAiModel('gpt-4o-mini');

  // Tour planning data
  const [planningData, setPlanningData] = useState({
    city: '',
    country: '',
    theme: '',
    targetAudience: '',
    numberOfStops: '',
    isIntroductoryStop: true
  });

  // Add local state for tour generation
  const [isTourGeneratingLocal, setIsTourGeneratingLocal] = useState(false);

  // Tour generation hooks
  const { generateTourPlan } = useTourGeneration(openAiKey);

  // Regular state
  const [itinerary, setItinerary] = useState({
    metadata: {
      id: generateId(),
      name: '',
      description: '',
      data_source: '',
      country: '',
      city: '',
      coordinates: { latitude: '', longitude: '' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      language: language,
      tier: 'free',
      duration: '',
      difficulty: 'moderate',
      categories: [],
      tags: [],
      thumbnailUrl: '',
      authorId: '',
      authorName: '',
      rating: 0,
      reviewCount: 0,
      theme: '',
      isIntroductoryStop: true,
      guide_type: 'outdoor',
      auto_play_radius_meters: 50,
      ai_generated_personalization: false,
      feedbackCollect: false,
      map_display: DEFAULT_MAP_DISPLAY_SETTINGS
    },
    points: []
  });

  const [currentPoi, setCurrentPoi] = useState(initialPoiState);
  const [contextFiles, setContextFiles] = useState([]);
  const [contextText, setContextText] = useState('');
  const [errors, setErrors] = useState({});

  // Add state for translation
  const [targetLanguage, setTargetLanguage] = useState('en-US');
  const [translationModel, setTranslationModel] = useState(DEFAULT_TRANSLATION_MODEL);
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);
  const [translatingFields, setTranslatingFields] = useState({});
  const [translationProgress, setTranslationProgress] = useState([]);

  const [showPrompts, setShowPrompts] = useState(false);

  // B2B Client flag - NOT saved to JSON, only used for SQL tags
  const [b2bClient, setB2bClient] = useState(false);

  // Route legs computation state
  const [isComputingLegs, setIsComputingLegs] = useState(false);
  const [legsLog, setLegsLog] = useState([]);
  const [legsStatus, setLegsStatus] = useState(null); // 'success' | 'error' | null
  const [showLegsLog, setShowLegsLog] = useState(false);
  const [routeMode, setRouteMode] = useState('walking');
  const legsLogEndRef = useRef(null);

  // Custom Hooks
  const { uploadImage, isUploading } = useImageUpload();
  const { generateFullItinerary, isGenerating } = useItineraryContent(
    language,
    itinerary,
    openAiKey
  );
  const { generateFieldContent, isGenerating: isFieldGenerating } = useFieldGeneration(openAiKey);

  // Handle planning data changes
  const handlePlanningChange = (field, value) => {
    setPlanningData(prev => ({
      ...prev,
      [field]: value
    }));

    // Also update relevant metadata fields
    if (field === 'city') {
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          city: value
        }
      }));
    }

    if (field === 'country') {
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          country: value
        }
      }));
    }

    if (field === 'theme') {
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          theme: value
        }
      }));
    }
  };

  // Tour Generation Handlers
  const handleGenerateTourPlan = async () => {
    if (!planningData.city || !planningData.country || !planningData.theme || !planningData.targetAudience) {
      setErrors(prev => ({
        ...prev,
        generation: 'Please fill in all required fields (City, Country, Theme, Target Audience)'
      }));
      return;
    }

    try {
      setIsTourGeneratingLocal(true);
      setErrors(prev => ({ ...prev, generation: null }));

      const data = {
        location: `${planningData.city}, ${planningData.country}`,
        theme: planningData.theme,
        audience: planningData.targetAudience,
        stops: planningData.numberOfStops ? planningData.numberOfStops.toString() : 'flexible',
        isIntroductoryStop: planningData.isIntroductoryStop
      };

      const mainTourPrompt = `You are a highly skilled tour guide specializing in creating engaging tours for ${data.audience} in ${data.location}, focusing on ${data.theme}. Design a tour with ${data.stops} stops.

    Return the response in this exact JSON format WITHOUT any markdown formatting or code blocks:
    {
      "title": "Tour name",
      "introduction": "Tour introduction",
      "theme": "Tour theme",
      "targetAudience": "Target audience description",
      "duration": "Estimated total duration in hours",
      "stops": [
        {
          "number": 1,
          "name": "Name of the stop",
          "description": "Brief description",
          "category": "Historical/Cultural/etc",
          "estimatedDuration": "Duration in minutes",
          "inDepthDescriptionContext": "2-3 sentences suggesting specific aspects to focus on"
        }
      ]
    }

    Do not include any text outside of this JSON structure.`;

      const languageInstruction = createLanguageInstruction(language);
      const filledPrompt = mainTourPrompt + languageInstruction;

      const response = await generateTourPlan(filledPrompt, currentModel);

      const cleanedResponse = response
        .replace(/```json\n?|\n?```/g, '')
        .replace(/^[\s\n]+|[\s\n]+$/g, '')
        .trim();

      console.log("Cleaned Response:", cleanedResponse.substring(0, 100) + '...');

      try {
        const tourData = JSON.parse(cleanedResponse);

        const pois = tourData.stops.map((stop, index) => ({
          ...initialPoiState,
          id: generatePoiId(itinerary.metadata, itinerary.points.slice(0, index)),
          name: stop.name,
          description: stop.description,
          category: stop.category || planningData.theme,
          estimatedDuration: parseInt(stop.estimatedDuration) || 60,
          inDepthDescriptionContext: stop.inDepthDescriptionContext || '',
          language: language,
          coordinates: {
            latitude: '',
            longitude: ''
          },
          nextStop: {
            description: '',
            duration: '',
            transportOptions: []
          }
        }));

        setItinerary(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            name: tourData.title,
            description: tourData.introduction,
            theme: tourData.theme,
            targetAudience: tourData.targetAudience,
            duration: String(Math.ceil(parseFloat(tourData.duration))),
            city: planningData.city,
            country: planningData.country,
            language: language,
            isIntroductoryStop: planningData.isIntroductoryStop,
            updatedAt: new Date().toISOString()
          },
          points: pois
        }));

        setActiveStep(2);
        return response;
      } catch (parseError) {
        console.error("JSON Parse Error:", parseError);
        console.error("Failed to parse:", cleanedResponse);
        throw new Error(`Failed to parse JSON response: ${parseError.message}`);
      }
    } catch (error) {
      console.error('Error in handleGenerateTourPlan:', error);
      setErrors(prev => ({
        ...prev,
        generation: `Failed to generate tour plan: ${error.message}`
      }));
      throw error;
    } finally {
      setIsTourGeneratingLocal(false);
    }
  };

  // Enhanced field generation with language support
  const handleGenerateField = async (field, customPrompt = null) => {
    try {
      console.log(`Generating field with voice style: ${voiceStyle}`);
      console.log(`Generating field: ${field} for ${currentPoi.name || 'new POI'}`);
      console.log(`Using AI model: ${currentModel}`);
      console.log(`Generating in language: ${language}`);

      if (customPrompt) {
        console.log(`Using provided custom prompt for ${field}`);

        let finalPrompt = customPrompt;
        if (!finalPrompt.includes(language)) {
          const languageInstruction = createLanguageInstruction(language);
          finalPrompt += languageInstruction;
        }

        if (['historicalContext', 'culturalSignificance'].includes(field)) {
          const knowledgeContext = await buildKnowledgeContext(itinerary.metadata);
          if (knowledgeContext) {
            finalPrompt += `\n\nUse the uploaded knowledge document(s) below as the primary source.\nKeep the answer to 3 or 4 short bullet points.\nEach bullet must be one short sentence.\nDo not invent facts or add unsupported claims.\n\n${knowledgeContext}`;
          }
        }

        const generatedContent = await generateFieldContent(field, currentPoi, {
          city: itinerary.metadata.city,
          country: itinerary.metadata.country,
          theme: itinerary.metadata.theme,
          language: language,
          prompt: finalPrompt
        }, currentModel);

        setCurrentPoi(prev => ({
          ...prev,
          [field]: generatedContent,
          language: language
        }));

        return;
      }

      console.error(`No custom prompt provided for field: ${field}. This should not happen.`);

      const languageInstruction = createLanguageInstruction(language);
      let fieldPrompt = `Generate ${field} for point of interest "${currentPoi.name || 'New POI'}" in ${itinerary.metadata.city}, ${itinerary.metadata.country}. Theme: ${itinerary.metadata.theme}.${languageInstruction}`;

      if (['historicalContext', 'culturalSignificance'].includes(field)) {
        const knowledgeContext = await buildKnowledgeContext(itinerary.metadata);
        if (knowledgeContext) {
          fieldPrompt += `\n\nUse the uploaded knowledge document(s) below as the primary source.\nKeep the answer to 3 or 4 short bullet points.\nEach bullet must be one short sentence.\nDo not invent facts or add unsupported claims.\n\n${knowledgeContext}`;
        }
      }

      const generatedContent = await generateFieldContent(field, currentPoi, {
        city: itinerary.metadata.city,
        country: itinerary.metadata.country,
        theme: itinerary.metadata.theme,
        language: language,
        prompt: fieldPrompt
      }, currentModel);

      setCurrentPoi(prev => ({
        ...prev,
        [field]: generatedContent,
        language: language
      }));
    } catch (error) {
      console.error(`Error in handleGenerateField for field ${field}:`, error);
      setErrors(prev => ({
        ...prev,
        [field]: `Failed to generate content: ${error.message}`
      }));
    }
  };

  // Focused audio generation function
  const handleGeneratePoiAudio = async (poi, voiceId) => {
    console.log('[handleGeneratePoiAudio] Entered function. POI:', poi, 'Voice ID:', voiceId);

    if (!poi.inDepthDescription) {
      console.log('[handleGeneratePoiAudio] In-depth description missing.');
      setErrors(prev => ({
        ...prev,
        audio: 'Please generate or enter an in-depth description first'
      }));
      return;
    }

    try {
      console.log('[handleGeneratePoiAudio] Setting isGeneratingAudio to true.');
      setIsGeneratingAudio(true);

      const effectiveVoiceId = voiceId || getVoiceForLanguage(poi.language || language);
      console.log('[handleGeneratePoiAudio] Effective Voice ID:', effectiveVoiceId);

      console.log('[handleGeneratePoiAudio] Calling generatePoiAudio hook...');
      const audioResult = await generatePoiAudio(
        poi.inDepthDescription,
        poi,
        itinerary.metadata,
        effectiveVoiceId
      );
      console.log('[handleGeneratePoiAudio] Received audioResult from hook:', audioResult);

      if (audioResult && typeof audioResult.audioDuration === 'number' && audioResult.audioDuration > 0) {
        console.log('[handleGeneratePoiAudio] Valid audioDuration received:', audioResult.audioDuration);
        const estimatedDurationInMinutes = Math.ceil(audioResult.audioDuration / 60);
        console.log('[handleGeneratePoiAudio] Calculated estimatedDurationInMinutes:', estimatedDurationInMinutes);
        setCurrentPoi(prev => {
          console.log('[handleGeneratePoiAudio] Updating currentPoi with duration.');
          return {
            ...prev,
            audioUrl: audioResult.audioUrl,
            uncompressedAudioUrl: audioResult.uncompressedAudioUrl || audioResult.originalAudioUrl || prev.uncompressedAudioUrl || '',
            originalAudioUrl: audioResult.originalAudioUrl || audioResult.uncompressedAudioUrl || prev.originalAudioUrl || '',
            ttsVoice: effectiveVoiceId,
            estimatedDuration: estimatedDurationInMinutes
          };
        });

        if (poi.id) {
          console.log('[handleGeneratePoiAudio] Updating POI in itinerary list.');
          setItinerary(prev => ({
            ...prev,
            points: prev.points.map(point =>
              point.id === poi.id
                ? {
                  ...point,
                  audioUrl: audioResult.audioUrl,
                  uncompressedAudioUrl: audioResult.uncompressedAudioUrl || audioResult.originalAudioUrl || point.uncompressedAudioUrl || '',
                  originalAudioUrl: audioResult.originalAudioUrl || audioResult.uncompressedAudioUrl || point.originalAudioUrl || '',
                  ttsVoice: effectiveVoiceId,
                  estimatedDuration: estimatedDurationInMinutes
                }
                : point
            )
          }));
        }
      } else {
        console.error('[handleGeneratePoiAudio] Invalid or missing audioDuration in audioResult.');
        setCurrentPoi(prev => {
          console.log('[handleGeneratePoiAudio] Updating currentPoi without duration.');
          return {
            ...prev,
            audioUrl: audioResult?.audioUrl,
            uncompressedAudioUrl: audioResult?.uncompressedAudioUrl || audioResult?.originalAudioUrl || prev.uncompressedAudioUrl || '',
            originalAudioUrl: audioResult?.originalAudioUrl || audioResult?.uncompressedAudioUrl || prev.originalAudioUrl || '',
            ttsVoice: effectiveVoiceId
          };
        });
        if (poi.id) {
          console.log('[handleGeneratePoiAudio] Updating POI in itinerary list without duration.');
          setItinerary(prev => ({
            ...prev,
            points: prev.points.map(point =>
              point.id === poi.id
                ? {
                  ...point,
                  audioUrl: audioResult?.audioUrl,
                  uncompressedAudioUrl: audioResult?.uncompressedAudioUrl || audioResult?.originalAudioUrl || point.uncompressedAudioUrl || '',
                  originalAudioUrl: audioResult?.originalAudioUrl || audioResult?.uncompressedAudioUrl || point.originalAudioUrl || '',
                  ttsVoice: effectiveVoiceId
                }
                : point
            )
          }));
        }
      }
      console.log('[handleGeneratePoiAudio] Returning audioUrl:', audioResult?.audioUrl);
      return audioResult?.audioUrl;
    } catch (error) {
      console.error('[handleGeneratePoiAudio] Error caught in try block:', error);
      setErrors(prev => ({
        ...prev,
        audio: `Failed to generate audio: ${error.message}`
      }));
      throw error;
    } finally {
      console.log('[handleGeneratePoiAudio] Entered finally block. Setting isGeneratingAudio to false.');
      setIsGeneratingAudio(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const url = await uploadImage(file);
        if (url) {
          setCurrentPoi(prev => ({ ...prev, photoUrl: url }));
        }
      } catch (error) {
        setErrors(prev => ({ ...prev, upload: 'Failed to upload image' }));
      }
    }
  };

  const handleGenerateContent = async () => {
    if (!itinerary.metadata.city || !itinerary.metadata.country) {
      setErrors(prev => ({
        ...prev,
        generation: 'Please enter both city and country before generating content'
      }));
      return;
    }

    try {
      setErrors({});

      const contextWithLanguage = contextText + createLanguageInstruction(language);

      const response = await generateFullItinerary(contextWithLanguage, currentModel);

      if (response) {
        const transformedResponse = {
          ...response,
          duration: Number(response.duration) || 1,
          points: response.points.map(point => ({
            ...point,
            estimatedDuration: Number(point.estimatedDuration) || 60,
            coordinates: {
              latitude: Number(point.coordinates.latitude) || 0,
              longitude: Number(point.coordinates.longitude) || 0
            },
            nextStop: {
              ...point.nextStop,
              duration: Number(point.nextStop.duration) || 15
            },
            language: language
          }))
        };

        setItinerary(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            description: transformedResponse.description || '',
            theme: transformedResponse.theme || prev.metadata.theme,
            categories: transformedResponse.categories || [],
            tags: transformedResponse.tags || [],
            duration: transformedResponse.duration.toString(),
            language: language,
            updatedAt: new Date().toISOString()
          },
          points: transformedResponse.points.map((point, index) => ({
            ...point,
            id: generatePoiId(itinerary.metadata, itinerary.points.slice(0, index)),
            audioUrl: '',
            photoUrl: '',
            language: language
          }))
        }));
      }
    } catch (error) {
      console.error('Error generating content:', error);
      setErrors(prev => ({
        ...prev,
        generation: `Failed to generate content: ${error.message}`
      }));
    }
  };

  // Add POI without auto-generating audio
  const handleAddPoi = async (options = {}) => {
    const keepEditing = Boolean(options?.keepEditing);
    const validationErrors = validatePoi(currentPoi);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const timestamp = Date.now();
      const stopNumber = itinerary.points.length > 0
        ? Math.max(...itinerary.points.map(point => {
            const stopNumberPart = point.id.split('_')[1];
            return stopNumberPart && !isNaN(parseInt(stopNumberPart)) ? parseInt(stopNumberPart) : 0;
          })) + 1
        : 1;
      // Extract only the first 2 characters for QR code format (e.g., "pt-pt" -> "pt", "en-US" -> "en")
      const fullLanguageCode = itinerary.metadata.language || 'en';
      const languageCode = fullLanguageCode.split(/[-_]/)[0].toLowerCase();
      const id = `${timestamp}_${stopNumber}_${languageCode}`;

      const newPoi = {
        ...currentPoi,
        id: id,
        tier: currentPoi.tier || 'free',
        language: currentPoi.language || language,
      };

      const updatedItinerary = {
        ...itinerary,
        points: [...itinerary.points, newPoi],
        metadata: {
          ...itinerary.metadata,
          updatedAt: new Date().toISOString()
        }
      };

      setItinerary(updatedItinerary);
      await saveItineraryToFile(updatedItinerary);
      setErrors({});
      if (!keepEditing) {
        setCurrentPoi(initialPoiState);
        setEditingPoi(false);
      }
    } catch (error) {
      console.error('Error adding POI:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message
      }));
    }
  };

  // Update POI without auto-generating audio
  const handleUpdatePoi = async (options = {}) => {
    const keepEditing = Boolean(options?.keepEditing);
    const validationErrors = validatePoi(currentPoi);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const updatedPoi = {
        ...currentPoi,
        id: currentPoi.id || generatePoiId(itinerary.metadata, itinerary.points),
        tier: currentPoi.tier || 'free',
        language: currentPoi.language || language,
        ttsVoice: currentPoi.ttsVoice || getVoiceForLanguage(currentPoi.language || language || 'en'),
      };

      const updatedItinerary = {
        ...itinerary,
        points: itinerary.points.map(point =>
          point.id === currentPoi.id ? updatedPoi : point
        ),
        metadata: {
          ...itinerary.metadata,
          updatedAt: new Date().toISOString()
        }
      };

      setItinerary(updatedItinerary);
      await saveItineraryToFile(updatedItinerary);
      setErrors({});
      if (!keepEditing) {
        setCurrentPoi(initialPoiState);
        setEditingPoi(false);
      }
    } catch (error) {
      console.error('Error updating POI:', error);
      setErrors(prev => ({
        ...prev,
        update: 'Failed to update POI'
      }));
    }
  };

  const handleReorderPOIs = (dragIndex, dropIndex) => {
    setItinerary(prev => {
      const newPoints = [...prev.points];
      const [draggedItem] = newPoints.splice(dragIndex, 1);
      newPoints.splice(dropIndex, 0, draggedItem);
      return {
        ...prev,
        points: newPoints,
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      };
    });
  };

  // Updated safe file saving function
  const saveItineraryToFile = async (itineraryData) => {
    try {
      let filename;

      if (itineraryData.metadata.filename) {
        filename = itineraryData.metadata.filename;

        if (!filename.toLowerCase().endsWith('.json')) {
          filename += '.json';
        }
      } else {
        const sanitizeFilename = (name) => {
          return (name || 'itinerary')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        };

        const country = itineraryData.metadata.country || '';
        const name = itineraryData.metadata.name || 'itinerary';

        const sanitizedCountry = sanitizeFilename(country);
        const sanitizedName = sanitizeFilename(name);

        filename = `${sanitizedCountry}_${language}_${sanitizedName}.json`;
      }

      const itineraryDataWithTranslationInfo = prepareItineraryForPublication({
        ...itineraryData,
        metadata: {
          ...itineraryData.metadata,
          parent_guide_id: itineraryData.metadata.parent_guide_id,
          translated_itinerary: itineraryData.metadata.translated_itinerary || "no"
        }
      });
      const fileContent = JSON.stringify(itineraryDataWithTranslationInfo, null, 2);

      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON File',
              accept: { 'application/json': ['.json'] }
            }],
          });

          const writable = await handle.createWritable();
          await writable.write(fileContent);
          await writable.close();
        } catch (fsError) {
          console.log("File System API failed or cancelled, using Blob fallback");
          SafeDOM.downloadFile(fileContent, filename);
        }
      } else {
        SafeDOM.downloadFile(fileContent, filename);
      }
    } catch (error) {
      console.error('Error saving itinerary:', error);
      alert('Failed to save itinerary. Please try again.');
    }
  };

  const handleLoad = async (event) => {
    try {
      const file = event?.target?.files?.[0];
      if (!file) return;

      const formattedItinerary = await loadItineraryFromFile(file, itinerary.metadata);

      if (formattedItinerary) {
        const fixedMetadata = {
          ...formattedItinerary.metadata,
          feedbackCollect: formattedItinerary.metadata.feedbackCollect ?? false,
          isIntroductoryStop: formattedItinerary.metadata.isIntroductoryStop ?? false,
          ai_generated_personalization: formattedItinerary.metadata.ai_generated_personalization ?? false
        };

        setItinerary({
          ...formattedItinerary,
          metadata: fixedMetadata
        });
        
        setPlanningData(prev => ({
          ...prev,
          city: fixedMetadata.city,
          country: fixedMetadata.country,
          theme: fixedMetadata.theme,
          isIntroductoryStop: fixedMetadata.isIntroductoryStop
        }));
        
        console.log('Loaded itinerary with isIntroductoryStop:', fixedMetadata.isIntroductoryStop);

        if (formattedItinerary.metadata.language) {
          setLanguage(formattedItinerary.metadata.language);
        }

        setErrors({});
      }

      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      console.error('Error loading file:', error);
      setErrors(prev => ({ ...prev, load: 'Failed to load itinerary' }));

      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  const handleComputeLegs = async () => {
    setIsComputingLegs(true);
    setLegsLog([]);
    setLegsStatus(null);
    setShowLegsLog(true);

    const appendLog = (msg) => {
      setLegsLog((prev) => [...prev, msg]);
      setTimeout(() => legsLogEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };

    try {
      appendLog('Starting route leg computation…');
      const enriched = await computeRoutelegs(itinerary, appendLog, routeMode);

      appendLog('Saving enriched guide to Firebase…');
      const sanitizedItinerary = {
        ...enriched,
        points: enriched.points.map((point) => ({
          ...point,
          tier: point.tier || 'free',
          language: point.language || itinerary.metadata.language || 'en',
        })),
      };

      await saveItineraryToStorage(sanitizedItinerary, {
        ...itinerary.metadata,
        has_qr_codes: itinerary.metadata.has_qr_codes ?? false,
        forceUpdate: true,
      });

      setItinerary(enriched);
      appendLog(`Guide saved successfully with ${enriched.legs?.length ?? 0} leg(s).`);
      setLegsStatus('success');
    } catch (err) {
      appendLog(`Fatal error: ${err.message}`);
      setLegsStatus('error');
    } finally {
      setIsComputingLegs(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!itinerary.metadata.data_source || !String(itinerary.metadata.data_source).trim()) {
        setErrors(prev => ({
          ...prev,
          save: 'Please fill in the Data Source field before saving the itinerary.'
        }));
        return;
      }

      const currentItinerary = {
        ...itinerary,
        metadata: {
          ...itinerary.metadata,
          updatedAt: new Date().toISOString(),
          language: language,
          has_qr_codes: itinerary.metadata.has_qr_codes ?? false
        },
        points: itinerary.points.map(point => ({
          ...point,
          tier: point.tier || 'free',
          language: point.language || language || itinerary.metadata.language || 'en',
        }))
      };

      await saveItineraryToFile(currentItinerary);
    } catch (error) {
      setErrors(prev => ({ ...prev, save: 'Failed to save itinerary' }));
    }
  };

  // Handle POI edit request
  const handleEditPoi = (poi) => {
    setCurrentPoi(poi);
    setEditingPoi(true);

    if (poi.language) {
      setLanguage(poi.language);
    }
  };

  // Handle new POI creation
  const handleNewPoi = () => {
    setCurrentPoi({
      ...initialPoiState,
      language: language
    });
    setEditingPoi(true);
  };

  // Handle translating all POIs in the itinerary
  const handleTranslateAllItinerary = async () => {
    if (!targetLanguage) {
      setErrors(prev => ({ ...prev, translation: 'Please select a target language for translation.' }));
      return;
    }
  
    setIsTranslatingAll(true);
    setErrors(prev => ({ ...prev, translation: null }));
    setTranslationProgress([]);
  
    const updatedPoints = [];
    let translationFailed = false;

    const addProgress = (message) => {
      console.log(message);
      setTranslationProgress(prev => [...prev, message]);
    };

    try {
      addProgress(`Starting translation to ${getLanguageName(targetLanguage)}...`);
      
      const { name: itineraryName, description: itineraryDescription } = itinerary.metadata;
      let translatedName = itineraryName;
      let translatedDescription = itineraryDescription;
      
      try {
        if (itineraryName) {
          addProgress(`Translating itinerary name: "${itineraryName}"`);
          
          translatedName = await generateFieldContent('itinerary_name', 
            { name: itineraryName }, 
            {
              city: itinerary.metadata.city,
              country: itinerary.metadata.country,
              theme: itinerary.metadata.theme,
              language: targetLanguage,
              prompt: `Translate ONLY this title to ${targetLanguage}. Return ONLY the translated title: "${itineraryName}"`
            }, 
            translationModel
          );
          
          if (translatedName.length > itineraryName.length * 3 || 
              translatedName.includes('\n') || 
              translatedName.length > 200) {
            addProgress('Name translation seems incorrect, trying simpler approach...');
            
            translatedName = await generateFieldContent('itinerary_name',
              { name: itineraryName },
              {
                language: targetLanguage,
                prompt: `Translate only this title, nothing more: "${itineraryName}"`
              },
              translationModel
            );
            
            if (translatedName.length > itineraryName.length * 3 || 
                translatedName.length > 200) {
              addProgress('Itinerary name translation failed, using original name');
              translatedName = itineraryName;
            }
          }
          
          translatedName = translatedName.replace(/^["']|["']$/g, '').trim();
          addProgress(`✓ Translated itinerary name: "${translatedName}"`);
        }
        
        if (itineraryDescription) {
          addProgress('Translating itinerary description...');
          translatedDescription = await generateFieldContent('itinerary_description',
            { description: itineraryDescription },
            {
              city: itinerary.metadata.city,
              country: itinerary.metadata.country,
              theme: itinerary.metadata.theme,
              language: targetLanguage,
              prompt: `${DESCRIPTION_TRANSLATION_INSTRUCTIONS}\n\nOriginal description:\n"${itineraryDescription}"\n\nTranslated description (ONLY):`
            },
            translationModel
          );
          addProgress('✓ Itinerary description translated');
        }
      } catch (error) {
        addProgress(`⚠️ Failed to translate itinerary metadata: ${error.message}`);
      }

      addProgress(`Translating ${itinerary.points.length} points of interest...`);
      
      for (const [index, poi] of itinerary.points.entries()) {
        setTranslatingFields(prev => ({ ...prev, [poi.id]: true }));
        try {
          addProgress(`[${index + 1}/${itinerary.points.length}] Translating POI: "${poi.name}"`);
          const translatedPoi = await translatePoiFields(poi, targetLanguage, translationModel);
          addProgress(`✓ Completed: "${poi.name}" → "${translatedPoi.name}"`);
          updatedPoints.push(translatedPoi);
        } catch (error) {
          addProgress(`⚠️ Failed to translate POI "${poi.name}": ${error.message}`);
          setErrors(prev => ({
            ...prev,
            translation: `Failed to process "${poi.name}": ${error.message}`
          }));
          updatedPoints.push(poi);
          translationFailed = true;
        } finally {
          setTranslatingFields(prev => ({ ...prev, [poi.id]: false }));
        }
      }

      setItinerary(prev => ({
        ...prev,
        points: updatedPoints,
        metadata: {
          ...prev.metadata,
          name: translatedName,
          description: translatedDescription,
          updatedAt: new Date().toISOString(),
          language: translationFailed ? prev.metadata.language : targetLanguage
        }
      }));
      
      if (!translationFailed) {
        addProgress(`✅ Translation complete! Itinerary is now in ${getLanguageName(targetLanguage)}`);
      } else {
        addProgress(`⚠️ Translation partially completed with some errors`);
      }
      
    } catch (error) {
      addProgress(`❌ Translation process failed: ${error.message}`);
      setErrors(prev => ({
        ...prev,
        translation: `Translation process failed: ${error.message}`
      }));
    } finally {
      setIsTranslatingAll(false);
    }
  };

  // Handler for when batch audio generation is complete
  const handleBatchAudioComplete = async (updatedPois, stats) => {
    console.log('🎊 handleBatchAudioComplete called');
    console.log('🎊 Stats:', stats);
    const operation = stats?.mode || 'generation';
    console.log('🎊 Received updatedPois:', updatedPois.map(p => ({
      id: p.id,
      name: p.name,
      audioUrl: p.audioUrl,
      hasAudioUrl: !!p.audioUrl
    })));

    try {
      // IMPORTANT: Use setItinerary with a function to get the latest state
      // This ensures we're working with the most recent state after individual updates
      let finalItinerary;
      setItinerary(currentItinerary => {
        console.log('🎊 Current itinerary state at batch complete:', currentItinerary.points.map(p => ({
          id: p.id,
          name: p.name,
          hasAudioUrl: !!p.audioUrl
        })));

        const updatedItinerary = {
          ...currentItinerary,
          points: currentItinerary.points.map(originalPoi => {
            const updatedPoi = updatedPois.find(updated => updated.id === originalPoi.id);
            if (updatedPoi) {
              console.log('🎊 Mapping originalPoi', originalPoi.name, 'to updatedPoi with audioUrl:', updatedPoi.audioUrl);
            }
            return updatedPoi || originalPoi;
          }),
        metadata: {
          ...currentItinerary.metadata,
          updatedAt: new Date().toISOString(),
          ...(operation === 'conversion'
            ? {
                lastAudioConversion: {
                  timestamp: new Date().toISOString(),
                  language: updatedPois[0]?.language || 'en',
                  voice: updatedPois[0]?.ttsVoice || getVoiceForLanguage(updatedPois[0]?.language || language || 'en'),
                  stats: stats
                }
              }
            : {
                lastAudioGeneration: {
                  timestamp: new Date().toISOString(),
                  language: updatedPois[0]?.language || 'en',
                  voice: updatedPois[0]?.ttsVoice || getVoiceForLanguage(updatedPois[0]?.language || language || 'en'),
                  stats: stats
                }
              })
        }
      };

        console.log('🎊 Final updated itinerary points:', updatedItinerary.points.map(p => ({
          id: p.id,
          name: p.name,
          hasAudioUrl: !!p.audioUrl
        })));

        finalItinerary = updatedItinerary;
        return updatedItinerary;
      });

      // Save the updated itinerary to file
      try {
        await saveItineraryToFile(finalItinerary);
        console.log('✅ Itinerary auto-saved after batch audio generation');
      } catch (saveError) {
        console.warn('⚠️ Failed to auto-save itinerary:', saveError);
      }

      if (stats.successful > 0) {
        if (operation === 'conversion') {
          alert(`🎉 Batch audio conversion complete!\n\n✅ ${stats.successful}/${stats.total} audio files converted successfully\n⏱️ Total time: ${stats.totalTime}s\n💾 Itinerary has been saved automatically`);
        } else {
          alert(`🎉 Batch audio generation complete!\n\n✅ ${stats.successful}/${stats.total} audio files generated successfully\n⏱️ Total time: ${stats.totalTime}s\n💾 Itinerary has been saved automatically`);
        }
      } else {
        alert(`❌ Batch audio ${operation === 'conversion' ? 'conversion' : 'generation'} failed.\n\nNo audio files were processed successfully. Please check the logs for details.`);
      }

    } catch (error) {
      console.error('Error handling batch audio completion:', error);
      alert('There was an error updating the itinerary after audio generation. Please save manually.');
    }
  };

  // Handler for individual POI audio completion (real-time updates)
  const handleIndividualAudioComplete = (updatedPoi, currentIndex, totalPois) => {
    console.log(`🔔 handleIndividualAudioComplete called for POI ${currentIndex}/${totalPois}: ${updatedPoi.name}`);
    console.log('🔔 Received updatedPoi:', {
      id: updatedPoi.id,
      name: updatedPoi.name,
      audioUrl: updatedPoi.audioUrl,
      hasAudioUrl: !!updatedPoi.audioUrl
    });

    setItinerary(prev => {
      console.log('🔔 Current itinerary points:', prev.points.map(p => ({
        id: p.id,
        name: p.name,
        hasAudioUrl: !!p.audioUrl
      })));

      const updatedPoints = prev.points.map(poi => {
        if (poi.id === updatedPoi.id) {
          const merged = {
            ...poi,
            ...updatedPoi,
            description: poi.description || updatedPoi.description,
            inDepthDescription: poi.inDepthDescription || updatedPoi.inDepthDescription
          };
          console.log('🔔 Merged POI:', {
            id: merged.id,
            name: merged.name,
            audioUrl: merged.audioUrl,
            hasAudioUrl: !!merged.audioUrl
          });
          return merged;
        }
        return poi;
      });

      console.log('🔔 Updated points after merge:', updatedPoints.map(p => ({
        id: p.id,
        name: p.name,
        hasAudioUrl: !!p.audioUrl
      })));

      return {
        ...prev,
        points: updatedPoints,
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      };
    });

    if (currentIndex === totalPois) {
      console.log('✅ All individual audio generations complete');
    }
  };

  // Function to check if point IDs are in the correct format
  const checkPointIdFormat = (pointId, metadata) => {
    if (!pointId || !metadata) return false;

    // Get the expected guide identifier (parent_guide_id takes priority over metadata.id)
    const expectedGuideId = metadata.parent_guide_id || metadata.id;
    if (!expectedGuideId) return false;

    // Format: {guide_id}_{stop_sequence}_{language_code}
    // - guide_id: metadata.id or parent_guide_id
    // - stop_sequence: numeric sequence
    // - language_code: exactly 2 lowercase letters
    const pattern = new RegExp(`^${expectedGuideId}_\\d+_[a-z]{2}$`);
    return pattern.test(pointId);
  };

  // Function to regenerate all point IDs to the correct format
  const regeneratePointIds = () => {
    // Get the guide identifier (parent_guide_id takes priority over metadata.id)
    const guideId = itinerary.metadata.parent_guide_id || itinerary.metadata.id;

    if (!guideId) {
      console.error('Cannot regenerate POI IDs: No metadata.id or parent_guide_id found');
      setErrors(prev => ({ ...prev, regenerate: 'Guide ID is required for ID generation' }));
      return;
    }

    // Extract only the first 2 characters for language code (e.g., "pt-pt" -> "pt", "en-US" -> "en")
    const fullLanguage = String(itinerary.metadata.language || 'en').toLowerCase();
    const languageCode = fullLanguage.split(/[-_]/)[0];

    // Generate new POI IDs following the format: {guide_id}_{stop_sequence}_{language_code}
    const updatedPoints = itinerary.points.map((point, index) => ({
      ...point,
      id: `${guideId}_${index + 1}_${languageCode}`
    }));

    setItinerary(prev => ({
      ...prev,
      points: updatedPoints,
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));

    setErrors(prev => ({ ...prev, regenerate: null }));
  };

  const renderTourGenerator = () => {
    return (
      <div className="space-y-6">
        {getFieldAccess('ai_settings') !== 'hidden' && (
          <FeatureGuard featureName="ai_settings" requiredAccess="view_only">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <ModelSelector
            selectedModel={currentModel}
            onModelChange={changeModel}
                disabled={!hasFieldAccess('ai_settings', 'edit')}
          />
        </div>
          </FeatureGuard>
        )}

        <FeatureGuard featureName="tourPlanning" requiredAccess="edit">
        <TourPlanningSection
          planningData={planningData}
          onPlanningChange={handlePlanningChange}
          onGenerateTourPlan={handleGenerateTourPlan}
          isTourGenerating={isTourGeneratingLocal}
          error={errors.generation}
          showPrompts={showPrompts}
          setShowPrompts={setShowPrompts}
          voiceStyle={voiceStyle}
          onVoiceChange={setVoiceStyle}
          selectedModel={currentModel}
          onModelChange={changeModel}
        />
        </FeatureGuard>

        <FeatureGuard featureName="tourPlanning" requiredAccess="edit">
        <div>
          <button
            onClick={() => setShowContextPanel(!showContextPanel)}
            className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center justify-between"
          >
            <span className="font-medium">Additional Context (Optional)</span>
            <span>{showContextPanel ? '▲' : '▼'}</span>
          </button>

          {showContextPanel && (
            <div className="mt-4 p-4 border border-gray-200 rounded-lg">
              <ContextSection
                contextFiles={contextFiles}
                contextText={contextText}
                isGenerating={isGenerating}
                onGenerateContent={handleGenerateContent}
                onFileChange={(e) => setContextFiles(Array.from(e.target.files))}
                onTextChange={(e) => setContextText(e.target.value)}
              />
            </div>
          )}
        </div>
        </FeatureGuard>
      </div>
    );
  };

  const renderMainContent = () => {
    if (editingPoi) {
      return (
        <EnhancedPOIForm
          poi={currentPoi}
          onPoiChange={setCurrentPoi}
          onGenerateField={handleGenerateField}
          onGenerateAudio={handleGeneratePoiAudio}
          isGenerating={isFieldGenerating}
          isGeneratingAudio={isGeneratingAudio}
          errors={errors}
          onSubmit={currentPoi.id ? handleUpdatePoi : handleAddPoi}
          categories={POI_CATEGORIES}
          onImageUpload={handleImageUpload}
          isUploading={isUploading}
          itineraryMetadata={itinerary.metadata}
          itineraryPoints={itinerary.points}
          voiceStyle={voiceStyle}
          selectedModel={currentModel}
          onModelChange={changeModel}
          targetLanguage={targetLanguage}
          translationModel={translationModel}
          isTranslatingAll={isTranslatingAll}
          translatingFields={translatingFields}
          onTranslateAll={() => handleTranslateAllItinerary([currentPoi])}
        />
      );
    }

    switch (activeStep) {
      case 1:
        return renderTourGenerator();
      case 2:
        return (
          <MetadataSection
            itinerary={itinerary}
            setItinerary={setItinerary}
            b2bClient={b2bClient}
            setB2bClient={setB2bClient}
            errors={errors}
          />
        );
      case 3:
        return (
          <FeatureGuard featureName="ragManagement" requiredAccess="edit">
            <RAGDocumentManager
              itinerary={itinerary}
              setItinerary={setItinerary}
              onStatusChange={(status, message) => {
                console.log(`RAG Status: ${status} - ${message}`);
              }}
            />
          </FeatureGuard>
        );
      case 4:
        return (
          <StoryAITab
            itinerary={itinerary}
            setItinerary={setItinerary}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* VISIBLE TEST BANNER - This should be VERY visible */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'red',
        color: 'white',
        padding: '20px',
        textAlign: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        zIndex: 9999,
        border: '5px solid yellow'
      }}>
        🚨 TEST BANNER - DEV SERVER RESTARTED - ARE YOU SEEING THIS? 🚨
      </div>
      {/* SuperAdmin Permission Manager */}
{userRole === 'superadmin' && (
  <>
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={() => setShowPermissionManager(!showPermissionManager)}
        className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-purple-700 flex items-center gap-2"
      >
        🛡️ Permission Manager
      </button>
    </div>

    {showPermissionManager && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto">
        <div className="min-h-screen p-4">
          <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-xl relative">
            <button
              onClick={() => setShowPermissionManager(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
            <PermissionManager />
          </div>
        </div>
      </div>
    )}
  </>
)}

      <ErrorBoundary>
        <div className="flex h-screen bg-gray-100">
          {/* Left Panel - Building Tool */}
          <div className="w-1/2 overflow-y-auto p-6 border-r border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">Itinerary Builder</h1>

              <div className="flex items-center gap-3">
                <input type="file" accept=".json" onChange={handleLoad} className="hidden" id="load-itinerary" />
                <FeatureGuard featureName="SaveLoad" requiredAccess="edit">
                  <label
                    htmlFor="load-itinerary"
                    className="cursor-pointer bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                  >
                    <Upload className="w-3 h-3" />
                    Load
                  </label>
                </FeatureGuard>
                <FeatureGuard featureName="SaveLoad" requiredAccess="edit">
                  <button
                    onClick={handleSave}
                    className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" />
                    Save
                  </button>
                </FeatureGuard>
                <FeatureGuard
                  featureName="tourPlanning"
                  requiredAccess="edit"
                  fallback={<AccessDenied requiredAccess="edit" feature="Tour Generation" />}
                >
                  <button
                    className={`py-2 px-4 flex items-center gap-2 ${activeStep === 1 ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
                    onClick={() => setActiveStep(1)}
                  >
                    <Workflow className="w-5 h-5" />
                    <span>Tour Generator</span>
                  </button>
                </FeatureGuard>
              </div>
            </div>

            <VoiceHeader
              selectedVoice={voiceStyle}
              onVoiceChange={setVoiceStyle}
            />

            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
                {Object.entries(errors).map(([key, error]) => (
                  <div key={key}>{error}</div>
                ))}
              </div>
            )}

            <div style={{background: 'yellow', padding: '10px', marginBottom: '10px'}}>
              DEBUG: editingPoi={editingPoi ? 'true' : 'false'}, activeStep={activeStep}
            </div>

            {!editingPoi && (
              <div className="flex border-b mb-6 overflow-x-auto">
                <button
                  className={`py-2 px-4 flex items-center gap-2 ${activeStep === 2 ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
                  onClick={() => setActiveStep(2)}
                >
                  <MapPin className="w-5 h-5" />
                  <span>Metadata</span>
                </button>
                <button
                  className={`py-2 px-4 flex items-center gap-2 ${activeStep === 3 ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
                  onClick={() => {
                    console.log('RAG tab clicked, setting activeStep to 3');
                    setActiveStep(3);
                  }}
                >
                  <FileText className="w-5 h-5" />
                  <span>RAG Documents</span>
                </button>
                <button
                  className={`py-2 px-4 flex items-center gap-2 ${activeStep === 4 ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
                  onClick={() => setActiveStep(4)}
                >
                  <Workflow className="w-5 h-5" />
                  <span>Story AI</span>
                </button>
                <span style={{color: 'red', fontSize: '12px', marginLeft: '10px'}}>
                  Debug: editingPoi={editingPoi ? 'true' : 'false'}, activeStep={activeStep}
                </span>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm p-6">
              {renderMainContent()}
            </div>

            {!editingPoi && (
              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                  disabled={activeStep === 1}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400"
                >
                  Back
                </button>
                <button
                  onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
                >
                  Next Step
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {editingPoi && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setEditingPoi(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel Editing
                </button>
              </div>
            )}
          </div>

          {/* Right Panel - Preview & POI Management */}
          <div className="w-1/2 overflow-y-auto p-6 bg-gray-50">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <FeatureGuard featureName="addNewPoi" requiredAccess="edit">
                <button
                  onClick={handleNewPoi}
                  className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                >
                  <PlusCircle className="w-3 h-3" />
                  Add New POI
                </button>
                </FeatureGuard>

                <select
                  value={routeMode}
                  onChange={(e) => setRouteMode(e.target.value)}
                  className="border border-gray-300 rounded text-sm px-2 py-1 bg-white"
                >
                  {ROUTE_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                <button
                  onClick={handleComputeLegs}
                  disabled={isComputingLegs || itinerary.points.length === 0 || !itinerary.metadata.country || !itinerary.metadata.name}
                  className="bg-purple-500 text-white px-3 py-1 rounded text-sm hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-1"
                  title="Pre-compute route legs between stops and save to Firebase"
                >
                  {isComputingLegs ? <Loader className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                  {isComputingLegs ? 'Computing…' : 'Compute Route Legs'}
                </button>
              </div>
            </div>

            {showLegsLog && (
              <div className="mb-4 border rounded overflow-hidden">
                <div className="flex justify-between items-center px-3 py-2 bg-purple-50 border-b">
                  <span className="text-xs font-medium text-purple-800">Route Leg Log</span>
                  <button onClick={() => setShowLegsLog(false)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                </div>
                <div className="bg-gray-900 text-gray-100 p-3 max-h-40 overflow-y-auto text-xs font-mono">
                  {legsLog.map((line, i) => (
                    <div key={i} className="leading-5 whitespace-pre-wrap">{line}</div>
                  ))}
                  <div ref={legsLogEndRef} />
                </div>
                {legsStatus === 'success' && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 text-xs border-t">
                    <Check className="w-3 h-3" /> Route legs saved successfully.
                  </div>
                )}
                {legsStatus === 'error' && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs border-t">
                    <AlertCircle className="w-3 h-3" /> Finished with errors — see log above.
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Translation and Audio Controls */}
            <div className="space-y-4">
              {/* Translation Controls */}
              <FeatureGuard
                featureName="translation"
                requiredAccess="edit"
                fallback={<AccessDenied requiredAccess="edit" feature="Translation" />}
              >
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-4">
                  <h3 className="text-lg font-semibold">Translate Itinerary</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Target Language:</label>
                      <select
                        value={targetLanguage}
                        onChange={(e) => setTargetLanguage(e.target.value)}
                        className="p-2 border rounded w-full"
                        disabled={isTranslatingAll}
                      >
                        {LANGUAGES.map(lang => (
                          <option key={lang.code} value={lang.code}>
                            {lang.flag} {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <TranslationModelSelector
                        selectedTranslationModel={translationModel}
                        onTranslationModelChange={setTranslationModel}
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleTranslateAllItinerary}
                    disabled={isTranslatingAll || itinerary.points.length === 0}
                    className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isTranslatingAll ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Translating...
                      </>
                    ) : (
                      <>
                        <Languages className="w-4 h-4" />
                        Translate All POIs
                      </>
                    )}
                  </button>
                  
                  {(isTranslatingAll || translationProgress.length > 0) && (
                    <TranslationProgress
                      messages={translationProgress}
                      isTranslating={isTranslatingAll}
                    />
                  )}
                  
                  {errors.translation && (
                    <p className="text-red-500 text-sm mt-2">{errors.translation}</p>
                  )}
                </div>
              </FeatureGuard>

              {/* Batch Audio Generation */}
              <FeatureGuard
                featureName="audioGeneration"
                requiredAccess="edit"
                fallback={<AccessDenied requiredAccess="edit" feature="Batch Audio Generation" />}
              >
                <div className="bg-purple-50 rounded-lg border border-purple-200">
                  <button
                    onClick={() => setShowBatchAudio(!showBatchAudio)}
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-purple-100 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-800">Batch Audio Generation</span>
                      <span className="text-sm text-purple-600">
                        ({itinerary.points?.filter(p => p.inDepthDescription).length || 0} POIs ready)
                      </span>
                    </div>
                    <span className="text-purple-600">
                      {showBatchAudio ? '▼' : '▶'}
                    </span>
                  </button>
                  
                  {showBatchAudio && (
                    <div className="px-4 pb-4">
                      <BatchAudioGenerator
                        itinerary={itinerary}
                        onBatchComplete={handleBatchAudioComplete}
                        onIndividualComplete={handleIndividualAudioComplete}
                      />
                    </div>
                  )}
                </div>
              </FeatureGuard>
            </div>

            {/* Audio Generation Status */}
            {itinerary.points && itinerary.points.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded border">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Audio Status</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total POIs:</span>
                    <span className="ml-2 font-medium">{itinerary.points.length}</span>
                  </div>
                  <div>
                    <span className="text-green-600">With Audio:</span>
                    <span className="ml-2 font-medium">
                      {itinerary.points.filter(p => p.audioUrl).length}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-600">Ready for Audio:</span>
                    <span className="ml-2 font-medium">
                      {itinerary.points.filter(p => p.inDepthDescription && !p.audioUrl).length}
                    </span>
                  </div>
                </div>
                {itinerary.metadata.lastAudioGeneration && (
                  <div className="mt-2 text-xs text-gray-500">
                    Last generation: {new Date(itinerary.metadata.lastAudioGeneration.timestamp).toLocaleString()}
                    {itinerary.metadata.lastAudioGeneration.language && (
                      <span className="ml-2">
                        ({itinerary.metadata.lastAudioGeneration.language.toUpperCase()})
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-8">
              {itinerary.points.length > 0 && (
                <ItineraryMapView
                  points={itinerary.points}
                  itineraryMetadata={itinerary.metadata}
                />
              )}

              <div style={{background: 'yellow', padding: '20px', margin: '10px 0', border: '3px solid red'}}>
                <h3 style={{color: 'red', fontSize: '24px'}}>🟡 TEST RAG TAB 🟡</h3>
                <p>This is a test tab to see if tabs work here</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-semibold mb-4">Manage Points of Interest</h3>
                {itinerary.points.length > 0 ? (
                  <POIList
                    points={itinerary.points}
                    onEdit={handleEditPoi}
                    onRemove={(id) => {
                      const poiToDelete = itinerary.points.find(p => p.id === id);
                      const poiName = poiToDelete ? poiToDelete.name : 'this point of interest';
                      if (window.confirm(`Are you sure you want to delete "${poiName}"? This action cannot be undone.`)) {
                        setItinerary(prev => ({
                          ...prev,
                          points: prev.points.filter(p => p.id !== id),
                          metadata: {
                            ...prev.metadata,
                            updatedAt: new Date().toISOString()
                          }
                        }));
                      }
                    }}
                    onReorder={handleReorderPOIs}
                    isTranslatingAll={isTranslatingAll}
                    translatingFields={translatingFields}
                    checkPointIdFormat={checkPointIdFormat}
                    regeneratePointIds={regeneratePointIds}
                    itineraryMetadata={itinerary.metadata}
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No points of interest added yet. Use the "Add New POI" button to create one.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </>
  );
};

export default ItineraryCreator;
